/**
 * resolveActiveTransition — picks the concrete `ProposalTransition`
 * row that should drive the next page-transition animation.
 *
 * Resolution order:
 *   1. Per-slide override (`slide.pageTransitionId`) — takes precedence
 *      over the deck-level default.
 *   2. Deck-level default (`pageTransitionId` on the modal).
 *   3. `null` → caller renders a plain opacity cross-fade.
 *
 * The resolution is pure: it only inspects the inputs and looks up the
 * row in the catalogue by id. Returning `null` (rather than throwing)
 * keeps the UI flow forgiving when a transition was deleted while a
 * template was saved.
 */
import type { ProposalTransition } from '@/lib/transitions/catalogue';

export function resolveActiveTransition(args: {
  slideTransitionId?: string | null;
  deckTransitionId?: string | null;
  transitions: ProposalTransition[];
}): ProposalTransition | null {
  const candidate = args.slideTransitionId ?? args.deckTransitionId ?? null;
  if (!candidate) return null;
  return args.transitions.find((t) => t.id === candidate) ?? null;
}

/**
 * Stable signature for the deck-level page-transition + per-slide
 * overrides. Used by the preview hook to bump `playbackKey` when the
 * admin flips a picker, without resetting playback head.
 */
export function buildTransitionSignature(
  slides: ReadonlyArray<{ pageTransitionId?: string | null }>,
  deckId?: string | null,
): string {
  const per = slides
    .map((s, idx) => `${idx}:${s.pageTransitionId ?? ''}`)
    .join('|');
  return `${per}|${deckId ?? ''}`;
}
